// Sprechen und Zuhören im Browser.
//
// Beides kommt vom Gerät selbst, nicht vom Server: Die Web Speech API erkennt
// gesprochenen Text und liest Antworten vor. Das kostet nichts, verlässt das
// Gerät nicht und ist sofort da — im Lager mit dem Handy in der einen und
// einem Karton in der anderen Hand genau richtig.
//
// Der Preis dafür: nicht jeder Browser kann es. Chrome, Edge und Safari
// erkennen Sprache, Firefox nicht. Deshalb fragt die Oberfläche vorher mit
// diktatMoeglich() nach und blendet das Mikrofon sonst aus, statt einen Knopf
// anzubieten, der beim Drücken nichts tut.

type Erkenner = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: SpeechErgebnis) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
};

type SpeechErgebnis = {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
};

type MitSpeech = typeof globalThis & {
  SpeechRecognition?: new () => Erkenner;
  webkitSpeechRecognition?: new () => Erkenner;
};

function ErkennerKlasse() {
  const g = globalThis as MitSpeech;
  return g.SpeechRecognition || g.webkitSpeechRecognition || null;
}

export function diktatMoeglich(): boolean {
  return !!ErkennerKlasse();
}

export function vorlesenMoeglich(): boolean {
  return typeof globalThis !== 'undefined' && 'speechSynthesis' in globalThis;
}

/** Sprachkennungen für die Erkennung — regional, sonst hört sie schlecht zu. */
const GEBIETSSCHEMA: Record<string, string> = {
  de: 'de-DE', en: 'en-GB', fr: 'fr-FR', es: 'es-ES',
  tr: 'tr-TR', ar: 'ar-SA', zh: 'zh-CN',
};

export type DiktatLauscher = {
  /** Zwischenstand — was gerade erkannt wird, noch nicht endgültig. */
  beiZwischenstand?: (text: string) => void;
  /** Endgültig erkannter Satz. */
  beiErgebnis: (text: string) => void;
  beiFehler?: (meldung: string) => void;
  beiEnde?: () => void;
};

/**
 * Startet die Spracherkennung. Gibt eine Funktion zum Beenden zurück —
 * oder null, wenn der Browser nicht mitspielt.
 */
export function diktatStarten(sprache: string, lauscher: DiktatLauscher): (() => void) | null {
  const Klasse = ErkennerKlasse();
  if (!Klasse) return null;

  const erkenner = new Klasse();
  erkenner.lang = GEBIETSSCHEMA[sprache] || 'de-DE';
  // Fortlaufend, damit auch ein längerer Satz mit Denkpause ankommt.
  erkenner.continuous = true;
  erkenner.interimResults = true;
  erkenner.maxAlternatives = 1;

  erkenner.onresult = (e) => {
    let endgueltig = '';
    let zwischen = '';
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const stueck = e.results[i][0]?.transcript ?? '';
      if (e.results[i].isFinal) endgueltig += stueck;
      else zwischen += stueck;
    }
    if (zwischen) lauscher.beiZwischenstand?.(zwischen);
    if (endgueltig.trim()) lauscher.beiErgebnis(endgueltig.trim());
  };

  erkenner.onerror = (e) => {
    // "aborted" ist kein Fehler, sondern unser eigenes Beenden.
    if (e.error === 'aborted') return;
    lauscher.beiFehler?.(fehlertext(e.error));
  };

  erkenner.onend = () => lauscher.beiEnde?.();

  try { erkenner.start(); } catch { return null; }
  return () => { try { erkenner.abort(); } catch { /* schon beendet */ } };
}

function fehlertext(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Das Mikrofon ist gesperrt. Bitte in den Browsereinstellungen für diese Seite erlauben.';
    case 'no-speech':
      return 'Nichts gehört. Bitte noch einmal sprechen.';
    case 'audio-capture':
      return 'Kein Mikrofon gefunden.';
    case 'network':
      return 'Die Spracherkennung braucht eine Internetverbindung.';
    default:
      return 'Die Spracherkennung ist ausgestiegen.';
  }
}

// ---------------------------------------------------------------------------
// Vorlesen
// ---------------------------------------------------------------------------

/**
 * Liest einen Text vor. Bricht Laufendes ab, damit sich zwei Antworten nicht
 * überlagern.
 */
export function vorlesen(text: string, sprache: string) {
  if (!vorlesenMoeglich() || !text.trim()) return;
  try {
    speechSynthesis.cancel();
    const aeusserung = new SpeechSynthesisUtterance(text);
    aeusserung.lang = GEBIETSSCHEMA[sprache] || 'de-DE';
    aeusserung.rate = 1.05;
    speechSynthesis.speak(aeusserung);
  } catch { /* Vorlesen ist Beiwerk, kein Grund für eine Fehlermeldung */ }
}

export function vorlesenStoppen() {
  if (!vorlesenMoeglich()) return;
  try { speechSynthesis.cancel(); } catch { /* egal */ }
}
